import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  LinearProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import BuildIcon from '@mui/icons-material/Build';
import CloseIcon from '@mui/icons-material/Close';
import api from '../services/api';
import jobWorksApi from '../api/jobWorks';
import { useNavigate } from 'react-router-dom';
import { productionApi } from '../api/production';

interface DesignProductionData {
  design_no: string;
  total_cut_qty: number;
  dhaga_qty: number;
  press_qty: number;
  qc_qty: number;
  in_repair_qty: number;
  permanent_damage_qty: number;
  barcoded_qty: number;
  remaining_qty: number;
  status: string;
  batches: Array<{
    batch_number: string;
    garment_part: string;
    total_issued: number;
  }>;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export default function ProductionTrackerPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [activeDesigns, setActiveDesigns] = useState<DesignProductionData[]>([]);
  const [completedDesigns, setCompletedDesigns] = useState<DesignProductionData[]>([]);
  const [pendingToReceive, setPendingToReceive] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedDesign, setSelectedDesign] = useState<DesignProductionData | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [designImages, setDesignImages] = useState<{ [key: string]: string }>({});
  const [currentTab, setCurrentTab] = useState(0);
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [selectedJobWork, setSelectedJobWork] = useState<any>(null);
  const [jwLoading, setJwLoading] = useState(false);
  const [jwSizesByPart, setJwSizesByPart] = useState<{ [part: string]: { [size: string]: number } }>({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch active and completed designs, and pending to receive
      const [activeData, completedData, pendingData] = await Promise.all([
        api.get('/production/stats/by-design?status=ACTIVE'),
        api.get('/production/stats/by-design?status=COMPLETED'),
        productionApi.getPendingToReceive()
      ]);

      setActiveDesigns(activeData.data);
      setCompletedDesigns(completedData.data);
      // Production Tracker only shows COMPLETED jobs (stitching done, ready for finishing unit)
      // ISSUED jobs appear in Design Cutting module
      const completedOnly = (pendingData || []).filter((job: any) => job.status === 'COMPLETED');
      setPendingToReceive(completedOnly);

      // Fetch design images for all designs
      const allDesigns = [...activeData.data, ...completedData.data];
      const uniqueDesignNos = [...new Set(allDesigns.map(d => d.design_no))];
      
      const imagePromises = uniqueDesignNos.map(async (designNo) => {
        try {
          const response = await api.get(`/designs/${designNo}/image`);
          if (response.data && response.data.imageUrl) {
            return { designNo, imageUrl: response.data.imageUrl };
          }
        } catch (err) {
          console.error(`Error fetching image for ${designNo}:`, err);
        }
        return null;
      });

      const imageResults = await Promise.all(imagePromises);
      const imageMap: { [key: string]: string } = {};
      imageResults.forEach(result => {
        if (result) {
          imageMap[result.designNo] = result.imageUrl;
        }
      });
      setDesignImages(imageMap);
    } catch (err: any) {
      console.error('Error fetching production data:', err);
      setError('Failed to load production data');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (design: DesignProductionData) => {
    setSelectedDesign(design);
    setDetailsOpen(true);
  };

  // When opening receive dialog, fetch job work details to show size-wise completion (what stitching sent)
  useEffect(() => {
    const load = async () => {
      if (!receiveDialogOpen || !selectedJobWork?.id) return;
      try {
        setJwLoading(true);
        const details = await jobWorksApi.getById(selectedJobWork.id);
        const byPart: { [part: string]: { [size: string]: number } } = {};
        (details.receivedItems || []).forEach((ri: any) => {
          const part = String(ri.garmentPart || '').toUpperCase();
          const size = String(ri.size || '').toUpperCase();
          if (!byPart[part]) byPart[part] = {};
          byPart[part][size] = (byPart[part][size] || 0) + (Number(ri.pieceCount) || 0);
        });
        setJwSizesByPart(byPart);
      } catch (e) {
        setJwSizesByPart({});
      } finally {
        setJwLoading(false);
      }
    };
    load();
  }, [receiveDialogOpen, selectedJobWork]);

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedDesign(null);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const getImageUrl = (designNo: string) => {
    return designImages[designNo] || '';
  };

  const handleStationClick = (station: string, designNo: string) => {
    // Redirect QC to the new QC station page
    if (station === 'qc') {
      navigate(`/production/qc?design=${designNo}`);
    } else {
      // Dhaga and other stations have been removed
      navigate(`/production/qc?design=${designNo}`);
    }
  };

  const handleRepairClick = (designNo: string) => {
    // Navigate to repair queue with design filter
    navigate(`/production/repairs?design=${designNo}`);
  };

  const renderDesignRow = (design: DesignProductionData) => {
    const imageUrl = getImageUrl(design.design_no);
    const completionPercentage = design.total_cut_qty > 0 
      ? Math.round((design.barcoded_qty / design.total_cut_qty) * 100) 
      : 0;

    return (
      <TableRow key={design.design_no} hover>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={imageUrl}
              variant="rounded"
              sx={{ width: 50, height: 50 }}
            >
              {design.design_no.substring(0, 2)}
            </Avatar>
            <Typography variant="body2" fontWeight="bold">
              {design.design_no}
            </Typography>
          </Box>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2" fontWeight="bold">
            {design.total_cut_qty}
          </Typography>
        </TableCell>
        <TableCell align="center">
          {design.dhaga_qty > 0 ? (
            <Tooltip title="Click to view in Dhaga Cutting station">
              <Chip 
                label={design.dhaga_qty} 
                color="warning" 
                size="small" 
                onClick={() => handleStationClick('dhaga', design.design_no)}
                sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
              />
            </Tooltip>
          ) : (
            <Typography variant="body2" color="text.secondary">-</Typography>
          )}
        </TableCell>
        <TableCell align="center">
          {design.press_qty > 0 ? (
            <Tooltip title="Click to view in Press station">
              <Chip 
                label={design.press_qty} 
                color="secondary" 
                size="small" 
                onClick={() => navigate(`/production/press?design=${design.design_no}`)}
                sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
              />
            </Tooltip>
          ) : (
            <Typography variant="body2" color="text.secondary">-</Typography>
          )}
        </TableCell>
        <TableCell align="center">
          {design.qc_qty > 0 ? (
            <Tooltip title="Click to view in QC station">
              <Chip 
                label={design.qc_qty} 
                color="primary" 
                size="small" 
                onClick={() => handleStationClick('qc', design.design_no)}
                sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
              />
            </Tooltip>
          ) : (
            <Typography variant="body2" color="text.secondary">-</Typography>
          )}
        </TableCell>
        <TableCell align="center">
          {design.in_repair_qty > 0 ? (
            <Tooltip title="Click to view in Repair Queue">
              <Chip 
                icon={<BuildIcon />} 
                label={design.in_repair_qty} 
                color="warning" 
                size="small" 
                onClick={() => handleRepairClick(design.design_no)}
                sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
              />
            </Tooltip>
          ) : (
            <Typography variant="body2" color="text.secondary">-</Typography>
          )}
        </TableCell>
        <TableCell align="center">
          {design.permanent_damage_qty > 0 ? (
            <Typography variant="body2" color="error.main" fontWeight="bold">
              {design.permanent_damage_qty}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">-</Typography>
          )}
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2" color="success.main" fontWeight="bold">
            {design.barcoded_qty}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
            <Typography variant="body2" fontWeight="bold">
              {design.remaining_qty}
            </Typography>
            <Box sx={{ flex: 1, minWidth: 60 }}>
              <LinearProgress 
                variant="determinate" 
                value={completionPercentage} 
                sx={{ height: 6, borderRadius: 1 }} 
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              {completionPercentage}%
            </Typography>
          </Box>
        </TableCell>
        <TableCell align="center">
          <Tooltip title="View Details">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleViewDetails(design)}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <LinearProgress sx={{ width: '50%' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Paper sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            Finishing Unit Tracker
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/production/opening-entry')}
          >
            Opening Entry
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab 
              label={`Pending to Receive (${pendingToReceive.length})`} 
              id="tab-0"
              aria-controls="tabpanel-0"
            />
            <Tab 
              label={`Active Designs (${activeDesigns.length})`} 
              id="tab-1"
              aria-controls="tabpanel-1"
            />
            <Tab 
              label={`Completed Designs (${completedDesigns.length})`} 
              id="tab-2"
              aria-controls="tabpanel-2"
            />
          </Tabs>
        </Box>

        {/* Pending to Receive Tab */}
        <TabPanel value={currentTab} index={0}>
          <TableContainer>
            <Table size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  <TableCell>Image</TableCell>
                  <TableCell>Job Work Code</TableCell>
                  <TableCell>Design</TableCell>
                  <TableCell>Vendor</TableCell>
                  <TableCell>Completed Date</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingToReceive.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Box sx={{ py: 4 }}>
                        <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">
                          No pending items to receive
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          All completed stitching jobs have been received!
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  pendingToReceive.map((jobWork) => {
                    const imageUrl = jobWork.design?.image_url || jobWork.design?.image2_url || 
                                    jobWork.design?.image3_url || jobWork.design?.image4_url;
                    return (
                      <TableRow key={jobWork.id} hover>
                        <TableCell>
                          <Avatar
                            src={imageUrl || ''}
                            variant="rounded"
                            sx={{ width: 50, height: 50 }}
                          >
                            {jobWork.designNo.substring(0, 2)}
                          </Avatar>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {jobWork.jobWorkCode}
                          </Typography>
                        </TableCell>
                        <TableCell>{jobWork.designNo}</TableCell>
                        <TableCell>{jobWork.vendor?.alias || jobWork.vendor?.registered_name}</TableCell>
                        <TableCell>
                          {jobWork.actualReturn ? new Date(jobWork.actualReturn).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => {
                              setSelectedJobWork(jobWork);
                              setReceiveDialogOpen(true);
                            }}
                          >
                            Receive
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Active Designs Tab */}
        <TabPanel value={currentTab} index={1}>
          <TableContainer>
            <Table size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  <TableCell>Design</TableCell>
                  <TableCell align="center">Total Cut</TableCell>
                  <TableCell align="center">Dhaga</TableCell>
                  <TableCell align="center">Press</TableCell>
                  <TableCell align="center">QC</TableCell>
                  <TableCell align="center">In Repair</TableCell>
                  <TableCell align="center">Damaged</TableCell>
                  <TableCell align="center">Barcoded</TableCell>
                  <TableCell align="center">Remaining</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeDesigns.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        No active designs found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  activeDesigns.map(design => renderDesignRow(design))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Completed Designs Tab */}
        <TabPanel value={currentTab} index={2}>
          <TableContainer>
            <Table size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  <TableCell>Design</TableCell>
                  <TableCell align="center">Total Cut</TableCell>
                  <TableCell align="center">Dhaga</TableCell>
                  <TableCell align="center">Press</TableCell>
                  <TableCell align="center">QC</TableCell>
                  <TableCell align="center">In Repair</TableCell>
                  <TableCell align="center">Damaged</TableCell>
                  <TableCell align="center">Barcoded</TableCell>
                  <TableCell align="center">Remaining</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {completedDesigns.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        No completed designs found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  completedDesigns.map(design => renderDesignRow(design))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Paper>

      {/* Details Dialog */}
      <Dialog 
        open={detailsOpen} 
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Design Details: {selectedDesign?.design_no}</Typography>
            <IconButton onClick={handleCloseDetails} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedDesign && (
            <Box>
              {/* Design Image */}
              {getImageUrl(selectedDesign.design_no) && (
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                  <img 
                    src={getImageUrl(selectedDesign.design_no)} 
                    alt={selectedDesign.design_no}
                    style={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain' }}
                  />
                </Box>
              )}

              {/* Summary */}
              <Typography variant="h6" gutterBottom>Summary</Typography>
              <Table size="small" sx={{ mb: 3 }}>
                <TableBody>
                  <TableRow>
                    <TableCell><strong>Total Cut Quantity:</strong></TableCell>
                    <TableCell align="right">{selectedDesign.total_cut_qty}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Barcoded:</strong></TableCell>
                    <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                      {selectedDesign.barcoded_qty}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Permanent Damage:</strong></TableCell>
                    <TableCell align="right" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                      {selectedDesign.permanent_damage_qty}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Remaining to Complete:</strong></TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {selectedDesign.remaining_qty}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              {/* Station-wise Breakdown */}
              <Typography variant="h6" gutterBottom>Station-wise Breakdown</Typography>
              <Table size="small" sx={{ mb: 3 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Station</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Dhaga Cutting</TableCell>
                    <TableCell align="right">{selectedDesign.dhaga_qty}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Press</TableCell>
                    <TableCell align="right">{selectedDesign.press_qty}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Final QC</TableCell>
                    <TableCell align="right">{selectedDesign.qc_qty}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>In Repair</TableCell>
                    <TableCell align="right" sx={{ color: 'warning.main' }}>
                      {selectedDesign.in_repair_qty}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              {/* Batches */}
              <Typography variant="h6" gutterBottom>Batches</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Batch Number</TableCell>
                    <TableCell>Garment Part</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedDesign.batches.map((batch, index) => (
                    <TableRow key={index}>
                      <TableCell>{batch.batch_number}</TableCell>
                      <TableCell>{batch.garment_part || 'TOP'}</TableCell>
                      <TableCell align="right">{batch.total_issued}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Receive Dialog - Simplified: Navigate to job work details */}
      <Dialog
        open={receiveDialogOpen}
        onClose={() => {
          setReceiveDialogOpen(false);
          setSelectedJobWork(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Receive Completed Stitching Job</DialogTitle>
        <DialogContent>
          {selectedJobWork && (
            <Box sx={{ py: 2 }}>
              <Typography variant="body1" gutterBottom>
                <strong>Job Work Code:</strong> {selectedJobWork.jobWorkCode}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Design:</strong> {selectedJobWork.designNo}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Vendor:</strong> {selectedJobWork.vendor?.alias || selectedJobWork.vendor?.registered_name}
              </Typography>

              {/* Size-wise summary of what stitching sent */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Pieces sent by Stitching (Completed)
                </Typography>
                {jwLoading ? (
                  <Typography variant="body2" color="text.secondary">Loading...</Typography>
                ) : Object.keys(jwSizesByPart).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No completion records found.</Typography>
                ) : (
                  Object.keys(jwSizesByPart).map(part => {
                    const sizes = jwSizesByPart[part];
                    const ordered = ['XS','S','M','L','XL','XXL','3XL'].filter(s => sizes[s]);
                    const total = ordered.reduce((sum, s) => sum + (sizes[s] || 0), 0);
                    return (
                      <Box key={part} sx={{ mb: 2 }}>
                        <Typography variant="subtitle2">{part}</Typography>
                        <Table size="small" sx={{ mt: 1 }}>
                          <TableHead>
                            <TableRow>
                              {ordered.map(s => <TableCell key={s} align="right">{s}</TableCell>)}
                              <TableCell align="right"><strong>Total</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              {ordered.map(s => <TableCell key={s} align="right">{sizes[s]}</TableCell>)}
                              <TableCell align="right"><strong>{total}</strong></TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </Box>
                    );
                  })
                )}
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Click "Receive at Dhaga Station" to create a pending batch in Dhaga Cutting for these pieces.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setReceiveDialogOpen(false);
            setSelectedJobWork(null);
          }}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              if (selectedJobWork) {
                navigate(`/job-works/${selectedJobWork.id}?returnTo=/production`);
              }
            }}
          >
            View Job Work
          </Button>
          <Button
            variant="contained"
            disabled={jwLoading || !selectedJobWork || Object.keys(jwSizesByPart).length === 0}
            onClick={async () => {
              if (!selectedJobWork) return;
              try {
                setJwLoading(true);
                // Call receive-from-stitching per garment part
                for (const part of Object.keys(jwSizesByPart)) {
                  await productionApi.receiveFromStitching({
                    job_work_id: selectedJobWork.id,
                    sizes: jwSizesByPart[part],
                    garment_part: part
                  });
                }
                // Remove from list and close
                setPendingToReceive(prev => prev.filter(jw => jw.id !== selectedJobWork.id));
                setSuccess('Received at Dhaga Station');
                setReceiveDialogOpen(false);
                setSelectedJobWork(null);
              } catch (e: any) {
                setError(e?.response?.data?.error || 'Failed to receive');
              } finally {
                setJwLoading(false);
              }
            }}
          >
            Receive at Dhaga Station
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
}
